import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { finalize, forkJoin } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { CommonService } from 'src/app/services/common.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { showFallbackHttpError } from 'src/app/shared/utils/http-error-toast.util';
import { ConstantUtil } from 'src/app/util/constant.util';

import { NumericQuotaAddonPurchaseOption } from './numeric-quota-addon-purchase-modal.component';
import { PointPackagePurchaseModalComponent } from './point-package-purchase-modal.component';
import { RetentionPurchaseModalComponent } from './retention-purchase-modal.component';

type ChargePolicyType = 'FixedDeny' | 'PointDeduct' | 'UpgradeHint' | 'PurchaseAddon' | 'PaidExtension';
type QuotaUsageStatus = 'Normal' | 'HighUsage' | 'Full' | 'OverLimit';
type RiskStatus = 'Normal' | 'Warning' | 'Danger';
type AuthorizationStatus = 'active' | 'revoked' | 'expired' | 'pending' | 'unknown';

interface PointBalanceSummary {
  ailePoint: number | null;
  uuponPoint: number | null;
  aipoolTenantId: string | null;
  lastSyncTime?: number | null;
  entryUrl?: string | null;
}

interface RiskSummary {
  overLimitCount?: number;
  highUsageCount?: number;
  lowBalanceAlert?: boolean;
  status?: RiskStatus;
}

interface UsageSummary {
  quotaItemCount?: number;
  overLimitCount?: number;
  deviationCount?: number;
  monthDeductPoints?: number;
  overallUsageRate?: number;
}

interface QuotaUsageItem {
  itemId?: string;
  quotaCode?: string;
  quotaName?: string;
  categoryCode?: string;
  categoryName?: string;
  freeLimitValue?: string;
  limitValue?: string | null;
  usedValue?: string | null;
  remainingValue?: string | null;
  usageRate?: number | null;
  unit?: string;
  pointDeductUnitPrice?: string | null;
  chargePolicyType?: ChargePolicyType | null;
  chargePolicyLabel?: string | null;
  status?: QuotaUsageStatus;
  overLimit?: boolean;
  displayHint?: string | null;
  lastUpdateTime?: number | null;
}

interface TenantQuotaViewDetail {
  tenantId?: string;
  tenantName?: string;
  planName?: string;
  snapshotTime?: number;
  pointBalance?: Partial<PointBalanceSummary> | null;
  riskSummary?: RiskSummary | null;
  usageSummary?: UsageSummary | null;
  items?: QuotaUsageItem[];
}

interface SummaryMetric {
  labelKey: string;
  value: string;
  hint: string;
  tone: 'blue' | 'orange' | 'red' | 'green';
}

interface AuthorizationAction {
  labelKey: string;
  kind: 'primary' | 'default';
  action: 'authorize' | 'unauthorize';
  openId?: string | null;
  actionUrl?: string | null;
}

interface AuthorizationSummary {
  status: AuthorizationStatus;
  statusLabelKey: string;
  description: string;
  pendingCount: number;
  pendingPoints: number;
  pendingItems: PendingDeductionItem[];
  canManage: boolean;
  primaryAction: AuthorizationAction | null;
  secondaryAction: AuthorizationAction | null;
}

interface PendingDeductionItem {
  time: number | null;
  timeLabel: string;
  type: string;
  points: number | null;
  status: string;
  note: string;
}

interface BookedDeductSummary {
  tenantId?: string;
  totalBookedPoints?: number | null;
  items?: unknown[];
}

interface RetentionOptionModalCloseResult {
  refresh?: boolean;
}

interface RetentionCard {
  quotaCode: string;
  purchaseCode: string;
  title: string;
  description: string;
  currentValue: string;
  currentHint: string;
  optionsText: string;
  actionLabelKey: string;
  actionUrl: string | null;
  previewLoading: boolean;
  preview: RetentionPreview | null;
  previewPayload: RetentionPurchasePayload | null;
}

export interface NumericQuotaAddonCard {
  quotaCode: string;
  title: string;
  description: string;
  unit: string;
  limitValue: string;
  usedValue: string;
  remainingValue: string;
  loading: boolean;
  previewLoading: boolean;
  availableOptions: string[];
  hasPendingOrder: boolean;
}

interface RetentionStatusChip {
  key: 'unpaid' | 'queued';
  text: string;
  tone: 'warning' | 'info';
}

type RetentionEntryFocus = 'unpaid' | 'queued' | null;

interface RetentionPreview {
  mode: 'free' | 'purchased';
  enabled: boolean;
  statusText: string;
  packageText: string;
  packageHint: string;
  availableOptions: string[];
  activePeriodText?: string | null;
  pendingText?: string | null;
  purchaseDateText?: string | null;
  expireDateText?: string | null;
  remainingDaysText?: string | null;
  statusChips: RetentionStatusChip[];
}

interface RetentionPrivilegeSnapshot {
  id?: string | null;
  retentionDays?: number | null;
  status?: string | null;
  orderId?: string | null;
  pricingCode?: string | null;
  pricingItemId?: string | null;
  priceCny?: number | null;
  startTime?: number | null;
  activateTime?: number | null;
  expireTime?: number | null;
  paidTime?: number | null;
  graceEndTime?: number | null;
  originalPriceCny?: number | null;
  upgradeCreditCny?: number | null;
  upgradeCreditPoints?: number | null;
  compensationAilePoints?: number | null;
}

interface RetentionUnpaidOrderSnapshot {
  id?: string | null;
  orderId?: string | null;
  retentionDays?: number | null;
  status?: string | null;
  pricingCode?: string | null;
  pricingItemId?: string | null;
  priceCny?: number | null;
  originalPriceCny?: number | null;
  upgradeCreditCny?: number | null;
  payableCash?: number | null;
  compensationAilePoints?: number | null;
  createTime?: number | null;
  payStartTime?: number | null;
  payExpireTime?: number | null;
  reserveTimeoutSeconds?: number | null;
  paymentUrl?: string | null;
  needPay?: boolean | null;
  latestOrderStatus?: string | null;
  paymentStatus?: string | null;
  actionType?: string | null;
  targetRetentionDays?: number | null;
  queueActivateTime?: number | null;
  paymentState?: string | null;
  activationState?: string | null;
  compensationRemark?: string | null;
  compensationTime?: number | null;
}

interface RetentionPrivilegePayload {
  quotaCode?: string | null;
  active?: RetentionPrivilegeSnapshot | null;
  pending?: RetentionPrivilegeSnapshot | null;
  queuedAddon?: RetentionPrivilegeSnapshot | null;
  unpaidOrder?: RetentionUnpaidOrderSnapshot | null;
}

interface RetentionPurchaseOption {
  ruleId?: string;
  name?: string;
  description?: string;
  retentionDays?: number;
  effectValue?: string;
  effectUnit?: string;
  extraDays?: number;
  price?: number;
  actionType?: string;
  estimatedUpgradeCreditCny?: number;
  estimatedPayableCash?: number;
  upgradeCreditCny?: number;
  payableCash?: number;
  priceHint?: string;
  unitPrice?: string;
  currency?: string;
  recommend?: boolean;
  sort?: number;
}

interface RetentionPurchasePayload {
  planId?: string;
  planName?: string;
  quotaCode?: string;
  quotaName?: string;
  freeDays?: number;
  defaultDays?: number;
  currentDays?: number;
  currentValue?: string;
  enabled?: boolean;
  originalPriceCny?: number;
  upgradeCreditCny?: number;
  upgradeCreditPoints?: number;
  options?: RetentionPurchaseOption[];
}

interface RecentRecord {
  time: number | null;
  typeKey: string;
  type: string;
  itemKey: string;
  item: string;
  description: string;
  status: string;
  statusTone: 'green' | 'orange' | 'red' | 'gray';
  amount: number | null;
  ailePointsAmount: number | null;
  uuponPointsAmount: number | null;
  cashAmount: number | null;
}

type RecentRecordStatus = 'success' | 'pending' | 'failed' | 'skipped' | 'booked' | 'processing' | 'unknown';

interface RecentRecordPage {
  items: RecentRecord[];
  total: number;
  pageIndex: number;
  pageSize: number;
  hasNextPage: boolean;
}

interface QuotaGroup {
  key: string;
  title: string;
  description: string;
  items: QuotaUsageItem[];
}

@Component({
  selector: 'quota-view',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzCardModule,
    NzEmptyModule,
    NzIconModule,
    NzModalModule,
    NzPaginationModule,
    NzProgressModule,
    NzSpinModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './quota-view.component.html',
  styleUrls: ['./quota-view.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotaViewComponent implements OnInit {
  private static readonly RETENTION_CODES = ['MESSAGE_RETENTION', 'ATTACHMENT_RETENTION'];
  private static readonly NUMERIC_ADDON_CODES = ['CUSTOMER_LIMIT', 'ENTRY_REASON_LIMIT'];
  private static readonly POINT_DEDUCT_POLICY: ChargePolicyType = 'PointDeduct';
  private static readonly BROADCAST_LIMIT_CODE = 'BROADCAST_LIMIT';
  private static readonly POINT_PACKAGE_PRICING_CODE = 'POINT_LIMIT';

  private readonly http = inject(HttpService);
  private readonly commonService = inject(CommonService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = false;
  tenantId = '';
  tenantName = '';
  detail: TenantQuotaViewDetail | null = null;
  pointBalance: PointBalanceSummary = {
    ailePoint: null,
    uuponPoint: null,
    aipoolTenantId: null,
    lastSyncTime: null,
    entryUrl: null
  };
  riskSummary: RiskSummary | null = null;
  usageSummary: UsageSummary | null = null;
  summaryMetrics: SummaryMetric[] = [];
  authorization: AuthorizationSummary | null = null;
  quotaGroups: QuotaGroup[] = [];
  retentionCards: RetentionCard[] = [];
  numericAddonCards: NumericQuotaAddonCard[] = [];
  recentRecords: RecentRecord[] = [];
  recentRecordsLoading = false;
  recentRecordsTotal = 0;
  recentRecordsPageIndex = 1;
  recentRecordsPageSize = 20;
  recentRecordsHasNextPage = false;
  pendingDetailsExpanded = false;
  authorizationActionLoading: 'authorize' | 'unauthorize' | null = null;
  readonly loadingCardPlaceholders = Array.from({ length: 3 });
  readonly loadingRowPlaceholders = Array.from({ length: 4 });

  ngOnInit(): void {
    const tenant = this.commonService.getCurrentTenant();
    this.tenantId = String(tenant?.id ?? localStorage.getItem('current_tenant_id') ?? '').trim();
    this.tenantName = String(tenant?.name ?? localStorage.getItem('current_tenant_name') ?? '').trim();
    this.loadData();
  }

  get hasQuotaGroups(): boolean {
    return this.quotaGroups.length > 0;
  }

  get showPendingSection(): boolean {
    return !!this.authorization && this.authorization.status !== 'active';
  }

  get showAuthorizedPointCard(): boolean {
    return !!this.authorization && this.authorization.status === 'active' && this.pointBalance.ailePoint !== null;
  }

  get hasPendingDetails(): boolean {
    return !!this.authorization?.pendingItems.length;
  }

  get shouldShowRecentRecordsPagination(): boolean {
    return this.recentRecordsTotal > this.recentRecordsPageSize;
  }

  get recentRecordsPagePointTotal(): number | null {
    const amounts = this.recentRecords.map(record => record.amount).filter((value): value is number => Number.isFinite(value));
    if (!amounts.length) {
      return null;
    }
    return amounts.reduce((sum, value) => sum + value, 0);
  }

  get recentRecordsPageCashTotal(): number | null {
    const amounts = this.recentRecords.map(record => record.cashAmount).filter((value): value is number => Number.isFinite(value));
    if (!amounts.length) {
      return null;
    }
    return amounts.reduce((sum, value) => sum + value, 0);
  }

  get recentRecordsLatestTime(): number | null {
    for (let index = this.recentRecords.length - 1; index >= 0; index -= 1) {
      const value = this.recentRecords[index]?.time;
      if (Number.isFinite(value)) {
        return value as number;
      }
    }
    return null;
  }

  t(key: string, params?: Record<string, unknown>): string {
    return this.i18n.fanyi(key, params);
  }

  private get currentLocale(): string | undefined {
    return this.i18n.currentLang || undefined;
  }

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '--';
    }
    return new Intl.NumberFormat(this.currentLocale, {
      maximumFractionDigits: 20
    }).format(value);
  }

  formatSignedNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '--';
    }
    const formatted = this.formatNumber(Math.abs(value));
    if (formatted === '--') {
      return '--';
    }
    return value > 0 ? `+${formatted}` : value < 0 ? `-${formatted}` : formatted;
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '--';
    }
    return `${this.t('pricing.package.currency.twd')} ${this.formatNumber(value)}`;
  }

  formatDateTime(value: number | null | undefined): string {
    if (!value || !Number.isFinite(value)) {
      return '--';
    }
    return new Intl.DateTimeFormat(this.currentLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(value);
  }

  getPendingItemTimeLabel(item: PendingDeductionItem | null | undefined): string {
    if (!item) {
      return '--';
    }
    if (item.timeLabel) {
      return item.timeLabel;
    }
    return this.formatDateTime(item.time);
  }

  formatDateOnly(value: number | null | undefined): string {
    if (!value || !Number.isFinite(value)) {
      return '--';
    }
    return new Intl.DateTimeFormat(this.currentLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(value);
  }

  togglePendingDetails(): void {
    if (!this.hasPendingDetails) {
      return;
    }
    this.pendingDetailsExpanded = !this.pendingDetailsExpanded;
  }

  getPendingDetailsToggleLabel(): string {
    return this.t(
      this.pendingDetailsExpanded ? 'quota.view.authorization.pending-details-collapse' : 'quota.view.authorization.pending-details-expand'
    );
  }

  formatQuotaValue(value: string | number | null | undefined, unit?: string | null): string {
    if (value === null || value === undefined || value === '') {
      return '--';
    }
    const normalizedUnit = String(unit ?? '').trim();
    return `${value}${normalizedUnit ? ` ${normalizedUnit}` : ''}`;
  }

  formatUsedQuotaValue(item: QuotaUsageItem): string {
    const storageUsageDisplay = this.formatSmallGbValue(item.usedValue, item.unit);
    return storageUsageDisplay ?? this.formatQuotaValue(item.usedValue, item.unit);
  }

  getQuotaHint(item?: QuotaUsageItem | null): string {
    if (!item) {
      return '';
    }

    const pointDeductHint = this.getPointDeductHint(item);
    if (pointDeductHint) {
      return pointDeductHint;
    }

    return this.toNullableString(item.displayHint) || '';
  }

  formatUsageRate(rate: number | null | undefined): string {
    if (rate === null || rate === undefined || !Number.isFinite(Number(rate))) {
      return '--';
    }
    return `${Number(rate).toFixed(0)}%`;
  }

  getChargePolicyLabel(type?: ChargePolicyType | null): string {
    const labelMap: Record<ChargePolicyType, string> = {
      FixedDeny: this.t('quota.policy.fixed'),
      PointDeduct: this.t('quota.policy.deduct-points'),
      UpgradeHint: this.t('quota.policy.upgrade-prompt'),
      PurchaseAddon: this.t('quota.policy.purchase-extension'),
      PaidExtension: this.t('quota.policy.paid-extension')
    };
    return type ? (labelMap[type] ?? type) : '--';
  }

  getStatusTone(status?: QuotaUsageStatus | null): 'green' | 'orange' | 'red' {
    if (status === 'OverLimit') return 'red';
    if (status === 'Full' || status === 'HighUsage') return 'orange';
    return 'green';
  }

  getRiskTone(status?: RiskStatus | null): 'green' | 'orange' | 'red' {
    if (status === 'Danger') return 'red';
    if (status === 'Warning') return 'orange';
    return 'green';
  }

  getStatusLabel(status?: QuotaUsageStatus | null): string {
    const labelMap: Record<QuotaUsageStatus, string> = {
      Normal: this.t('tenant.detail.quota-usage-status-normal'),
      HighUsage: this.t('tenant.detail.quota-usage-status-high-usage'),
      Full: this.t('tenant.detail.quota-usage-status-full'),
      OverLimit: this.t('tenant.detail.quota-usage-status-over-limit')
    };
    return status ? (labelMap[status] ?? status) : '--';
  }

  getProgressPercent(item?: QuotaUsageItem | null): number {
    if (this.isBroadcastPointDeductPolicy(item)) {
      return 0;
    }
    const rate = Number(item?.usageRate);
    if (!Number.isFinite(rate)) {
      return 0;
    }
    return Math.max(0, Math.min(rate, 100));
  }

  getProgressStrokeColor(status?: QuotaUsageStatus | null): string {
    if (status === 'OverLimit') return '#ff4d4f';
    if (status === 'Full' || status === 'HighUsage') return '#fa8c16';
    return '#52c41a';
  }

  getAuthorizationTone(status?: AuthorizationStatus | null): 'green' | 'orange' | 'red' | 'gray' {
    if (status === 'active') return 'green';
    if (status === 'pending') return 'orange';
    if (status === 'revoked' || status === 'expired') return 'red';
    return 'gray';
  }

  getRecordAmountClass(amount: number | null | undefined): string {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) {
      return '';
    }
    if (amount === 0) {
      return '';
    }
    return amount < 0 ? 'is-negative' : 'is-positive';
  }

  getRecentRecordTypeBadgeClass(type: string): string {
    const normalized = String(type ?? '').trim();
    if (normalized === this.t('quota.view.records.type.admin-grant')) {
      return 'quota-record__type-badge quota-record__type-badge--green';
    }
    if (
      normalized === this.t('quota.view.records.type.over-limit-deduct') ||
      normalized === this.t('quota.view.records.type.order-compensation')
    ) {
      return 'quota-record__type-badge quota-record__type-badge--orange';
    }
    if (normalized === this.t('quota.view.records.type.feature-consumption')) {
      return 'quota-record__type-badge quota-record__type-badge--blue';
    }
    return 'quota-record__type-badge';
  }

  onRecentRecordsPageIndexChange(pageIndex: number): void {
    if (!Number.isFinite(pageIndex) || pageIndex <= 0 || pageIndex === this.recentRecordsPageIndex) {
      return;
    }
    this.recentRecordsPageIndex = pageIndex;
    this.loadRecentRecordsPage();
  }

  onRecentRecordsPageSizeChange(pageSize: number): void {
    if (!Number.isFinite(pageSize) || pageSize <= 0 || pageSize === this.recentRecordsPageSize) {
      return;
    }
    this.recentRecordsPageSize = pageSize;
    this.recentRecordsPageIndex = 1;
    this.loadRecentRecordsPage();
  }

  refreshRecentRecords(): void {
    this.loadRecentRecordsPage();
  }

  isPointDeductPolicy(item?: QuotaUsageItem | null): boolean {
    return item?.chargePolicyType === QuotaViewComponent.POINT_DEDUCT_POLICY;
  }

  isBroadcastPointDeductPolicy(item?: QuotaUsageItem | null): boolean {
    return (
      this.isPointDeductPolicy(item) &&
      String(item?.quotaCode ?? '')
        .trim()
        .toUpperCase() === QuotaViewComponent.BROADCAST_LIMIT_CODE
    );
  }

  openAction(url: string | null | undefined): void {
    const normalized = String(url ?? '').trim();
    if (!normalized) {
      this.message.warning(this.t('quota.view.action.unavailable'));
      return;
    }
    window.open(normalized, '_blank', 'noopener,noreferrer');
  }

  handleAuthorizationAction(action: AuthorizationAction | null | undefined): void {
    if (!action || this.authorizationActionLoading) {
      return;
    }

    if (action.action === 'authorize') {
      const openId = String(action.openId ?? '').trim();
      this.authorizationActionLoading = 'authorize';
      const payload = openId ? { openId } : {};
      this.http
        .post(ConstantUtil.ADMIN_TENANT_V1_POINT_AUTH_AUTHORIZE, payload)
        .pipe(
          finalize(() => {
            this.authorizationActionLoading = null;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: res => {
            const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
            if (!isSuccess) {
              this.message.warning(res?.msg || this.t('quota.view.action.unavailable'));
              return;
            }
            this.applyAuthorizeResponse(res?.data);
            if (res?.msg) {
              this.message.success(res.msg);
            }
            this.loadData();
          },
          error: err => {
            this.message.warning(err?.error?.msg || this.t('quota.view.action.unavailable'));
          }
        });
      return;
    }

    this.authorizationActionLoading = 'unauthorize';
    this.http
      .post(ConstantUtil.ADMIN_TENANT_V1_POINT_AUTH_UNAUTHORIZE, {})
      .pipe(
        finalize(() => {
          this.authorizationActionLoading = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            this.message.warning(res?.msg || this.t('quota.view.action.unavailable'));
            return;
          }
          if (res?.msg) {
            this.message.success(res.msg);
          }
          this.loadData();
        },
        error: err => {
          this.message.warning(err?.error?.msg || this.t('quota.view.action.unavailable'));
        }
      });
  }

  openPointPackagePurchase(): void {
    if (!this.tenantId) {
      this.message.warning(this.t('quota.view.action.unavailable'));
      return;
    }
    this.http.post(ConstantUtil.ADMIN_TENANT_POINT_PACKAGE_PURCHASE_V1_OPTIONS, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) {
          this.message.warning(res?.msg || this.t('quota.view.action.unavailable'));
          return;
        }
        const payload = res?.data ?? {};
        const configMap = new Map<string, any>(
          (Array.isArray(payload?.configs) ? payload.configs : []).map((config: any) => [String(config?.id ?? '').trim(), config])
        );
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const options = items
          .map((item: any) => this.toPointPackageOption(item, configMap.get(String(item?.pricingConfigId ?? '').trim()) ?? null))
          .filter(Boolean)
          .sort(
            (left: Record<string, unknown> | null, right: Record<string, unknown> | null) =>
              Number(left?.['displayOrder']) - Number(right?.['displayOrder'])
          );
        if (!options.length) {
          this.message.warning(this.t('quota.view.action.unavailable'));
          return;
        }
        this.openPointPackageModal(options as any[]);
      },
      error: err => {
        this.message.warning(err?.error?.msg || this.t('quota.view.action.unavailable'));
      }
    });
  }

  openRetentionPurchase(card: RetentionCard, focus: RetentionEntryFocus = null): void {
    const purchaseQuotaCode = card.purchaseCode || this.toRetentionPurchaseCode(card.quotaCode);
    if (!this.tenantId || !purchaseQuotaCode) {
      this.message.warning(this.t('quota.view.action.unavailable'));
      return;
    }

    this.http
      .post(ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_PURCHASE_V1_INFO, {
        tenantId: this.tenantId,
        quotaCode: purchaseQuotaCode
      })
      .subscribe({
        next: res => {
          const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
          if (!isSuccess) {
            this.message.warning(res?.msg || this.t('quota.view.action.unavailable'));
            return;
          }

          const payload = (res?.data ?? res) as RetentionPurchasePayload;
          this.openRetentionOptionModal(card, payload, focus);
        },
        error: err => {
          this.message.warning(err?.error?.msg || this.t('quota.view.action.unavailable'));
        }
      });
  }

  private openRetentionOptionModal(card: RetentionCard, payload: RetentionPurchasePayload, focus: RetentionEntryFocus = null): void {
    let modalRef: NzModalRef = null as unknown as NzModalRef;
    modalRef = this.modal.create({
      nzContent: RetentionPurchaseModalComponent,
      nzWidth: 760,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzClassName: 'retention-purchase-modal-shell',
      nzOnCancel: () => modalRef.close({ refresh: true }),
      nzData: {
        quotaCode: this.toRetentionPurchaseCode(card.quotaCode),
        quotaName: String(payload?.quotaName ?? card.title ?? '').trim(),
        tenantId: this.tenantId,
        planId: this.toNullableString(payload?.planId),
        planName: this.toNullableString(payload?.planName),
        aipoolTenantId: this.pointBalance.aipoolTenantId,
        currentValue: this.resolveRetentionCurrentValue(payload, card.currentValue),
        currentDays: this.toNullableNumber(payload?.currentDays),
        defaultDays: this.toNullableNumber(payload?.defaultDays),
        freeDays: this.toNullableNumber(payload?.freeDays),
        enabled: this.toBoolean(payload?.enabled),
        originalPriceCny: this.toNullableNumber(payload?.originalPriceCny),
        upgradeCreditCny: this.toNullableNumber(payload?.upgradeCreditCny),
        upgradeCreditPoints: this.toNullableNumber(payload?.upgradeCreditPoints),
        options: Array.isArray(payload?.options) ? payload.options : [],
        entryFocus: focus
      }
    });

    modalRef.afterClose.subscribe((result: RetentionOptionModalCloseResult | undefined) => {
      if (result?.refresh) {
        this.loadData();
      }
    });
  }

  private openPointPackageModal(options: Array<Record<string, unknown>>): void {
    let modalRef: NzModalRef = null as unknown as NzModalRef;
    modalRef = this.modal.create({
      nzContent: PointPackagePurchaseModalComponent,
      nzWidth: 960,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzClassName: 'retention-purchase-modal-shell',
      nzOnCancel: () => modalRef.close({ refresh: true }),
      nzData: {
        tenantId: this.tenantId,
        aipoolTenantId: this.pointBalance.aipoolTenantId,
        pricingCode: QuotaViewComponent.POINT_PACKAGE_PRICING_CODE,
        options
      }
    });
    modalRef.afterClose.subscribe((result?: RetentionOptionModalCloseResult) => {
      if (result?.refresh) {
        this.loadData();
      }
    });
  }

  openNumericAddonPurchase(card: NumericQuotaAddonCard): void {
    if (!card?.quotaCode || card.loading) return;
    this.patchNumericAddonCard(card.quotaCode, current => ({ ...current, loading: true }));
    this.http
      .post(ConstantUtil.ADMIN_PRICING_PACKAGE_ITEM_V1_LIST, { pricingCode: card.quotaCode })
      .pipe(
        finalize(() => {
          this.patchNumericAddonCard(card.quotaCode, current => ({ ...current, loading: false }));
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          const isSuccess = !!response?.success || response?.status === 0 || response?.status === 200;
          if (!isSuccess) {
            this.message.warning(response?.msg || this.t('quota.view.action.unavailable'));
            return;
          }
          const raw = response?.data ?? [];
          const items = Array.isArray(raw) ? raw : (raw?.items ?? raw?.list ?? []);
          const options: NumericQuotaAddonPurchaseOption[] = items
            .map((item: unknown) => this.toNumericAddonOption(item, card.quotaCode))
            .filter((option: NumericQuotaAddonPurchaseOption | null): option is NumericQuotaAddonPurchaseOption => option !== null);
          if (!options.length) {
            this.message.warning(this.t('quota.view.numeric-addon.modal.empty'));
            return;
          }
          this.openNumericAddonModal(card, options);
        },
        error: error => {
          this.message.warning(error?.error?.msg || this.t('quota.view.action.unavailable'));
        }
      });
  }

  private openNumericAddonModal(card: NumericQuotaAddonCard, options: NumericQuotaAddonPurchaseOption[]): void {
    const modalRef = this.modal.create({
      nzContent: PointPackagePurchaseModalComponent,
      nzWidth: 760,
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzClassName: 'retention-purchase-modal-shell',
      nzData: {
        tenantId: this.tenantId,
        aipoolTenantId: this.pointBalance.aipoolTenantId,
        pricingCode: card.quotaCode,
        quotaName: card.title,
        unit: card.unit,
        productType: 'numeric-addon',
        options
      }
    });
    modalRef.afterClose.subscribe((result?: RetentionOptionModalCloseResult) => {
      this.loadData();
    });
  }

  private toNumericAddonOption(item: unknown, pricingCode: string): NumericQuotaAddonPurchaseOption | null {
    const raw = (item ?? {}) as Record<string, unknown>;
    if (raw['deleted'] === true) return null;
    const pricingConfigId = String(raw['pricingConfigId'] ?? '').trim();
    const pricingItemId = String(raw['pricingItemId'] ?? raw['id'] ?? '').trim();
    const addonValue = this.toNullableNumber(raw['addonValue']);
    if (!pricingConfigId || !pricingItemId || addonValue === null || addonValue <= 0) return null;
    const priceCny = this.toNullableNumber(raw['discountPriceCny'] ?? raw['priceCny']) ?? 0;
    return {
      pricingConfigId,
      pricingItemId,
      pricingCode,
      title:
        String(raw['name'] ?? raw['title'] ?? '').trim() ||
        this.t('quota.view.numeric-addon.modal.default-option', { value: this.formatNumber(addonValue) }),
      description: String(raw['description'] ?? '').trim(),
      addonValue,
      priceCny
    };
  }

  private patchNumericAddonCard(quotaCode: string, updater: (card: NumericQuotaAddonCard) => NumericQuotaAddonCard): void {
    this.numericAddonCards = this.numericAddonCards.map(card => (card.quotaCode === quotaCode ? updater(card) : card));
  }

  private toPointPackageOption(item: any, config?: any): Record<string, unknown> | null {
    const pricingConfigId = String(item?.pricingConfigId ?? '').trim();
    const pricingItemId = String(item?.pricingItemId ?? item?.id ?? '').trim();
    if (!pricingConfigId || !pricingItemId) {
      return null;
    }
    const basePoints = this.toNullableNumber(item?.basePoints ?? item?.pointCount ?? item?.points) ?? 0;
    const bonusPoints = this.toNullableNumber(item?.bonusPoints ?? item?.giftPoints) ?? 0;
    const totalPoints = basePoints + bonusPoints;
    const title =
      String(item?.name ?? item?.pricingName ?? item?.title ?? '').trim() ||
      (totalPoints > 0
        ? `${this.formatNumber(totalPoints)} ${this.t('quota.view.point-card.unit')}`
        : String(item?.pricingCode ?? pricingItemId).trim());
    const description = String(item?.description ?? item?.subTitle ?? '').trim();
    return {
      pricingConfigId,
      pricingItemId,
      pricingCode: String(item?.pricingCode ?? config?.pricingCode ?? QuotaViewComponent.POINT_PACKAGE_PRICING_CODE).trim(),
      pricingType: String(item?.pricingType ?? config?.pricingType ?? '').trim(),
      recommend: item?.recommend === true,
      title,
      description,
      basePoints,
      bonusPoints,
      displayOrder: this.toNullableNumber(item?.displayOrder ?? item?.sort) ?? Number.MAX_SAFE_INTEGER,
      priceCny: this.toNullableNumber(item?.discountPriceCny ?? item?.priceCny) ?? 0,
      totalPoints
    };
  }

  private loadData(): void {
    if (!this.tenantId) {
      this.resetState();
      return;
    }

    this.loading = true;
    const detailRequest = this.http.post(ConstantUtil.ADMIN_QUOTA_TENANT_V1_USAGE_DETAIL, { tenantId: this.tenantId });
    const bookedSummaryRequest = this.http.post(this.getBookedSummaryUrl(), undefined);
    const recentRecordsRequest = this.http.post(ConstantUtil.TENANT_QUOTA_DEDUCT_AUDIT_V1_LIST, this.buildRecentRecordsPayload());

    forkJoin({
      detailRes: detailRequest,
      bookedSummaryRes: bookedSummaryRequest,
      recentRecordsRes: recentRecordsRequest
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ detailRes, bookedSummaryRes, recentRecordsRes }) => {
          if (!detailRes?.success || !detailRes?.data) {
            this.resetState();
            showFallbackHttpError(this.message, detailRes?.msg, this.t('quota.view.load-failed'));
            return;
          }

          const bookedSummary = this.extractBookedSummary(bookedSummaryRes);
          const recentRecords = this.extractRecentRecordsPage(recentRecordsRes);
          this.applyDetail(detailRes.data as TenantQuotaViewDetail & Record<string, unknown>, bookedSummary, recentRecords);
        },
        error: err => {
          this.resetState();
          showFallbackHttpError(this.message, err?.error?.msg, this.t('quota.view.load-failed'));
        }
      });
  }

  private applyDetail(
    detail: TenantQuotaViewDetail & Record<string, unknown>,
    bookedSummary: BookedDeductSummary | null,
    recentRecordsPage: RecentRecordPage
  ): void {
    this.detail = detail;
    this.tenantName = String(detail.tenantName ?? this.tenantName ?? '').trim();
    this.pointBalance = this.mapPointBalance(detail);
    this.riskSummary = detail.riskSummary ?? null;
    this.usageSummary = this.mapUsageSummary(detail);
    const quotaItems = Array.isArray(detail.items) ? detail.items : [];
    this.authorization = this.mapAuthorization(detail, bookedSummary);
    this.retentionCards = this.buildRetentionCards(detail, quotaItems);
    this.loadRetentionPurchasePreviews(this.retentionCards);
    this.numericAddonCards = this.buildNumericAddonCards(quotaItems);
    this.loadNumericAddonPreviews(this.numericAddonCards);
    this.quotaGroups = this.buildQuotaGroups(quotaItems);
    this.applyRecentRecordsPage(recentRecordsPage);
    this.summaryMetrics = this.buildSummaryMetrics(detail, quotaItems);
  }

  private mapPointBalance(detail: Record<string, unknown>): PointBalanceSummary {
    const raw = this.pickFirstObject([detail['pointBalance']]);
    return {
      ailePoint: this.toNullableNumber(raw?.['ailePoint']) ?? this.pointBalance.ailePoint,
      uuponPoint: this.toNullableNumber(raw?.['uuponPoint']) ?? this.pointBalance.uuponPoint,
      aipoolTenantId: this.toNullableString(raw?.['aipoolTenantId']) ?? this.pointBalance.aipoolTenantId,
      lastSyncTime: this.toNullableNumber(raw?.['lastSyncTime']) ?? this.pointBalance.lastSyncTime,
      entryUrl:
        this.toNullableString(raw?.['entryUrl'] ?? detail['aipoolEntryUrl'] ?? detail['pointRechargeUrl']) ?? this.pointBalance.entryUrl
    };
  }

  private applyAuthorizeResponse(data: unknown): void {
    const raw = this.pickFirstObject([data]);
    if (!raw) {
      return;
    }

    this.pointBalance = {
      ...this.pointBalance,
      ailePoint: this.toNullableNumber(raw['ailePoint'] ?? raw['remainingPoint']) ?? this.pointBalance.ailePoint,
      aipoolTenantId: this.toNullableString(raw['aipoolTenantId']) ?? this.pointBalance.aipoolTenantId,
      entryUrl: this.toNullableString(raw['entryUrl'] ?? raw['authorizeUrl'] ?? raw['rebindUrl']) ?? this.pointBalance.entryUrl
    };
  }

  private loadRecentRecordsPage(): void {
    if (!this.tenantId) {
      this.applyRecentRecordsPage({
        items: [],
        total: 0,
        pageIndex: Math.max(this.recentRecordsPageIndex - 1, 0),
        pageSize: this.recentRecordsPageSize,
        hasNextPage: false
      });
      return;
    }

    this.recentRecordsLoading = true;
    this.http
      .post(ConstantUtil.TENANT_QUOTA_DEDUCT_AUDIT_V1_LIST, this.buildRecentRecordsPayload())
      .pipe(
        finalize(() => {
          this.recentRecordsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          this.applyRecentRecordsPage(this.extractRecentRecordsPage(response));
        },
        error: err => {
          showFallbackHttpError(this.message, err?.error?.msg, this.t('quota.view.load-failed'));
        }
      });
  }

  private mapUsageSummary(detail: Record<string, unknown>): UsageSummary | null {
    const raw = this.pickFirstObject([detail['usageSummary']]);
    if (!raw) {
      return null;
    }
    return {
      quotaItemCount: this.toNullableNumber(raw['quotaItemCount']) ?? undefined,
      overLimitCount: this.toNullableNumber(raw['overLimitCount']) ?? undefined,
      deviationCount: this.toNullableNumber(raw['deviationCount']) ?? undefined,
      monthDeductPoints: this.toNullableNumber(raw['monthDeductPoints']) ?? undefined,
      overallUsageRate: this.toNullableNumber(raw['overallUsageRate']) ?? undefined
    };
  }

  private mapAuthorization(detail: Record<string, unknown>, bookedSummary: BookedDeductSummary | null): AuthorizationSummary | null {
    const raw = this.pickFirstObject([detail['aipoolAuthorization'], detail['authorization'], detail['autoDebitAuthorization']]) ?? {};
    const pendingItems = this.mapPendingItems(
      this.pickFirstArray([
        bookedSummary?.items,
        raw['pendingItems'],
        detail['pendingDeductionItems'],
        detail['pendingDeductItems'],
        detail['pendingDeductions']
      ])
    );
    const status =
      this.resolveAuthorizationStatusFromFlag(raw, detail) ??
      this.toAuthorizationStatus(raw['status'] ?? detail['authorizationStatus'] ?? detail['aipoolAuthorizationStatus']);
    const pendingCount = this.toNullableNumber(
      bookedSummary?.items?.length ??
        raw['pendingCount'] ??
        detail['pendingDeductionCount'] ??
        detail['pendingCount'] ??
        pendingItems.length
    );
    const pendingPoints =
      this.toNullableNumber(
        bookedSummary?.totalBookedPoints ?? raw['pendingPoints'] ?? detail['pendingDeductionPoints'] ?? detail['pendingPoints']
      ) ?? pendingItems.reduce((sum, item) => sum + (item.points ?? 0), 0);
    const openId = this.pickFirstString([
      raw['openId'],
      raw['aipoolOpenId'],
      raw['userOpenId'],
      detail['openId'],
      detail['aipoolOpenId'],
      detail['userOpenId']
    ]);
    const actionUrl = this.pickFirstString([
      raw['rebindUrl'],
      raw['bindUrl'],
      raw['authorizeUrl'],
      raw['actionUrl'],
      detail['rebindUrl'],
      detail['bindUrl'],
      detail['authorizeUrl'],
      detail['actionUrl']
    ]);
    const explicitCanManage = this.pickFirstBoolean([raw['canManage'], raw['canOperate'], detail['canManageAuthorization']]);
    const canAuthorize = explicitCanManage ?? status !== 'active';
    const canUnauthorize = status === 'active';
    const canManage = explicitCanManage ?? (canAuthorize || canUnauthorize);
    const description =
      this.toNullableString(raw['description'] ?? detail['authorizationDescription']) || this.t('quota.view.authorization.default-desc');

    if (status === 'unknown' && pendingItems.length === 0 && !canManage && !description) {
      return null;
    }

    return {
      status,
      statusLabelKey: `quota.view.authorization.status.${status}`,
      description,
      pendingCount: pendingCount ?? pendingItems.length,
      pendingPoints,
      pendingItems,
      canManage,
      primaryAction: canUnauthorize
        ? {
            labelKey: 'quota.view.authorization.action.revoke',
            kind: 'primary',
            action: 'unauthorize'
          }
        : canAuthorize
          ? {
              labelKey: 'quota.view.authorization.action.authorize',
              kind: 'primary',
              action: 'authorize',
              openId,
              actionUrl
            }
          : null,
      secondaryAction: null
    };
  }

  private resolveAuthorizationStatusFromFlag(raw: Record<string, unknown>, detail: Record<string, unknown>): AuthorizationStatus | null {
    const authorized = this.pickFirstBoolean([
      raw['pointAuthorized'],
      raw['isAuthorized'],
      raw['authorized'],
      raw['auth'],
      raw['enableAuthorization'],
      detail['pointAuthorized'],
      detail['isAuthorized'],
      detail['authorized'],
      detail['isAipoolAuthorized'],
      detail['aipoolAuthorized'],
      detail['authorizationEnabled']
    ]);

    if (authorized === null) {
      return null;
    }

    return authorized ? 'active' : 'revoked';
  }

  private mapPendingItems(rawItems: unknown[]): PendingDeductionItem[] {
    return rawItems.map(item => {
      const current = (item ?? {}) as Record<string, unknown>;
      const quotaCode = this.toNullableString(
        current['quotaCode'] ?? current['itemCode'] ?? current['resourceCode'] ?? current['sourceBizId']
      );
      const type =
        this.toNullableString(current['type'] ?? current['quotaName'] ?? current['title']) ||
        this.resolveQuotaCodeLabel(quotaCode) ||
        quotaCode ||
        '--';
      const timeLabel =
        this.toNullableString(current['billingMonth'] ?? current['billMonth'] ?? current['month'] ?? current['period']) || '';
      const note = this.toNullableString(current['note'] ?? current['description'] ?? current['displayHint']) || '';
      return {
        time: this.toNullableNumber(current['time'] ?? current['createTime'] ?? current['occurTime']),
        timeLabel,
        type,
        points: this.toNullableNumber(
          current['points'] ?? current['deductPoints'] ?? current['amount'] ?? current['remainingAmount'] ?? current['bookedPoints']
        ),
        status: this.toNullableString(current['status'] ?? current['statusLabel']) || this.t('quota.view.authorization.pending-status'),
        note
      };
    });
  }

  private extractBookedSummary(response: unknown): BookedDeductSummary | null {
    const result = (response ?? {}) as Record<string, unknown>;
    const isSuccess = !!result['success'] || result['status'] === 0 || result['status'] === 200;
    if (!isSuccess) {
      return {
        totalBookedPoints: 0,
        items: []
      };
    }

    const data = this.pickFirstObject([result['data'], response]);
    if (!data) {
      return {
        totalBookedPoints: 0,
        items: []
      };
    }

    return {
      tenantId: this.toNullableString(data['tenantId']) ?? undefined,
      totalBookedPoints: this.toNullableNumber(data['totalBookedPoints'] ?? data['pendingPoints'] ?? data['bookedPoints']),
      items: this.pickFirstArray([data['items'], data['pendingItems'], data['records']])
    };
  }

  private getBookedSummaryUrl(): string {
    return ConstantUtil.TENANT_QUOTA_DEDUCT_BOOKED_V1_SUMMARY.replace('{tenantId}', encodeURIComponent(this.tenantId));
  }

  private buildRetentionCards(detail: Record<string, unknown>, items: QuotaUsageItem[]): RetentionCard[] {
    const retentionItems = items.filter(item =>
      QuotaViewComponent.RETENTION_CODES.some(code =>
        String(item.quotaCode ?? '')
          .toUpperCase()
          .includes(code)
      )
    );

    return retentionItems.map(item => {
      const code = String(item.quotaCode ?? '').toUpperCase();
      const isMessage = code.includes('MESSAGE');
      const optionsText = this.toNullableString(item.displayHint) || this.t('quota.view.retention.default-options');
      const actionUrl = this.resolveRetentionActionUrl(detail, item);

      return {
        quotaCode: String(item.quotaCode ?? '').trim(),
        purchaseCode: this.toRetentionPurchaseCode(item.quotaCode),
        title:
          item.quotaName || (isMessage ? this.t('quota.view.retention.message-title') : this.t('quota.view.retention.attachment-title')),
        description: this.toNullableString(item.displayHint) || this.t('quota.view.retention.default-desc'),
        currentValue: this.formatQuotaValue(item.limitValue ?? item.freeLimitValue, item.unit),
        currentHint: this.t('quota.view.retention.current-hint'),
        optionsText,
        actionLabelKey: 'quota.view.retention.action',
        actionUrl,
        previewLoading: !!this.tenantId && !!this.toRetentionPurchaseCode(item.quotaCode),
        preview: null,
        previewPayload: null
      };
    });
  }

  private buildNumericAddonCards(items: QuotaUsageItem[]): NumericQuotaAddonCard[] {
    return QuotaViewComponent.NUMERIC_ADDON_CODES.map(code =>
      items.find(
        item =>
          String(item.quotaCode ?? '')
            .trim()
            .toUpperCase() === code
      )
    )
      .filter((item: QuotaUsageItem | undefined): item is QuotaUsageItem => !!item)
      .map(item => ({
        quotaCode: String(item.quotaCode ?? '')
          .trim()
          .toUpperCase(),
        title: item.quotaName || this.resolveQuotaCodeLabel(item.quotaCode) || String(item.quotaCode ?? '').trim(),
        description: this.resolveNumericAddonDescription(item),
        unit: String(item.unit ?? '').trim(),
        limitValue: this.formatQuotaValue(item.limitValue ?? item.freeLimitValue, item.unit),
        usedValue: this.formatQuotaValue(item.usedValue, item.unit),
        remainingValue: this.formatQuotaValue(item.remainingValue, item.unit),
        loading: false,
        previewLoading: !!this.tenantId,
        availableOptions: [],
        hasPendingOrder: false
      }));
  }

  private resolveNumericAddonDescription(item: QuotaUsageItem): string {
    const displayHint = this.toNullableString(item.displayHint);
    if (!displayHint) return this.t('quota.view.numeric-addon.description');

    // Older quota records still carry the former team-upgrade copy. Personal
    // tenants now have a purchasable add-on flow, so do not let stale backend
    // hints override the current action guidance.
    const isLegacyUpgradeHint = /提示升级团队版|提示升級團隊版|upgrade to (the )?team plan/i.test(displayHint);
    return isLegacyUpgradeHint ? this.t('quota.view.numeric-addon.description') : displayHint;
  }

  private loadNumericAddonPreviews(cards: NumericQuotaAddonCard[]): void {
    cards.forEach(card => {
      const pricingUrl = ConstantUtil.ADMIN_PRICING_PACKAGE_ITEM_V1_LIST;
      const pendingUrl = `${ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_ORDER_V1_PENDING}?pricingCode=${encodeURIComponent(card.quotaCode)}`;
      forkJoin({
        pricing: this.http.post(pricingUrl, { pricingCode: card.quotaCode }),
        pending: this.http.post(pendingUrl, {})
      }).subscribe({
        next: ({ pricing, pending }) => {
          const rawItems = pricing?.data;
          const items = Array.isArray(rawItems) ? rawItems : (rawItems?.items ?? rawItems?.list ?? []);
          const options: NumericQuotaAddonPurchaseOption[] = items
            .map((item: unknown) => this.toNumericAddonOption(item, card.quotaCode))
            .filter((option: NumericQuotaAddonPurchaseOption | null): option is NumericQuotaAddonPurchaseOption => !!option);
          const pendingData = pending?.data ?? pending;
          const pendingOrderId = this.toNullableString(pendingData?.orderId ?? pendingData?.id);
          this.patchNumericAddonCard(card.quotaCode, current => ({
            ...current,
            previewLoading: false,
            availableOptions: options.map((option: NumericQuotaAddonPurchaseOption) => option.title),
            hasPendingOrder: !!pendingOrderId
          }));
          this.cdr.markForCheck();
        },
        error: () => {
          this.patchNumericAddonCard(card.quotaCode, current => ({ ...current, previewLoading: false }));
          this.cdr.markForCheck();
        }
      });
    });
  }

  private loadRetentionPurchasePreviews(cards: RetentionCard[]): void {
    cards.forEach((card, index) => {
      if (!this.tenantId || !card.purchaseCode) {
        return;
      }

      this.patchRetentionCard(index, current => ({
        ...current,
        previewLoading: true
      }));

      this.http
        .post(ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_PRIVILEGE_V1_CURRENT, {
          tenantId: this.tenantId,
          quotaCode: card.purchaseCode
        })
        .subscribe({
          next: privilegeRes => {
            const privilegePayload = this.extractRetentionPrivilegePayload(privilegeRes);
            this.http
              .post(ConstantUtil.ADMIN_TENANT_QUOTA_ENTITLEMENT_PURCHASE_V1_INFO, {
                tenantId: this.tenantId,
                quotaCode: card.purchaseCode
              })
              .subscribe({
                next: purchaseRes => {
                  const purchasePayload = this.extractRetentionPurchasePayload(purchaseRes);
                  this.patchRetentionCard(index, current => ({
                    ...current,
                    currentValue: this.resolveRetentionDisplayValue(privilegePayload, purchasePayload, current.currentValue),
                    description: this.resolveRetentionDescription(
                      privilegePayload,
                      purchasePayload,
                      current.currentValue,
                      current.description
                    ),
                    previewLoading: false,
                    previewPayload: purchasePayload,
                    preview: this.buildRetentionPreview(privilegePayload, purchasePayload, current.currentValue)
                  }));
                },
                error: () => {
                  this.patchRetentionCard(index, current => ({
                    ...current,
                    currentValue: this.resolveRetentionDisplayValue(privilegePayload, null, current.currentValue),
                    description: this.resolveRetentionDescription(privilegePayload, null, current.currentValue, current.description),
                    previewLoading: false,
                    previewPayload: null,
                    preview: this.buildRetentionPreview(privilegePayload, null, current.currentValue)
                  }));
                }
              });
          },
          error: () => {
            this.patchRetentionCard(index, current => ({
              ...current,
              previewLoading: false
            }));
          }
        });
    });
  }

  private patchRetentionCard(index: number, updater: (card: RetentionCard) => RetentionCard): void {
    const current = this.retentionCards[index];
    if (!current) {
      return;
    }
    this.retentionCards = this.retentionCards.map((card, currentIndex) => (currentIndex === index ? updater(current) : card));
    this.cdr.markForCheck();
  }

  private buildRetentionPreview(
    privilegePayload: RetentionPrivilegePayload | null,
    purchasePayload: RetentionPurchasePayload | null,
    fallbackValue: string
  ): RetentionPreview {
    const currentValue = purchasePayload ? this.resolveRetentionCurrentValue(purchasePayload, fallbackValue) : fallbackValue;
    const active = privilegePayload?.active ?? null;
    const pending = privilegePayload?.queuedAddon ?? privilegePayload?.pending ?? null;
    const unpaidOrder = privilegePayload?.unpaidOrder ?? null;
    const activeDays = this.toNullableNumber(active?.retentionDays);
    const pendingDays = this.toNullableNumber(pending?.retentionDays);
    const enabled = activeDays !== null && activeDays > 0;
    const options = Array.isArray(purchasePayload?.options) ? purchasePayload?.options : [];
    const availableOptions = options
      .map(option => this.toRetentionOptionLabel(option))
      .filter((label, index, labels) => !!label && labels.indexOf(label) === index);
    const matchedOption = activeDays !== null ? options.find(option => this.toNullableNumber(option?.retentionDays) === activeDays) : null;
    const activePeriodText = this.buildRetentionPeriodText(active);
    const pendingText = pendingDays !== null ? `${pendingDays} 天待生效` : null;
    const purchaseDateText = this.resolveRetentionSnapshotDate(active?.paidTime ?? active?.activateTime ?? active?.startTime);
    const expireDateText = this.resolveRetentionSnapshotDate(active?.expireTime);
    const remainingDaysText = this.resolveRetentionRemainingDays(active?.expireTime);
    const hasPrivilege = enabled || pendingDays !== null;
    const statusChips: RetentionStatusChip[] = [];
    if (unpaidOrder && !this.isRetentionUnpaidOrderClosed(unpaidOrder)) {
      statusChips.push({
        key: 'unpaid',
        text: '待支付 1 筆',
        tone: 'warning'
      });
    }
    if (pendingDays !== null) {
      statusChips.push({
        key: 'queued',
        text: '待生效 1 筆',
        tone: 'info'
      });
    }

    return {
      mode: hasPrivilege ? 'purchased' : 'free',
      enabled,
      statusText: enabled ? '已購買延長包' : '目前為免費配額',
      packageText: matchedOption
        ? this.toRetentionOptionLabel(matchedOption)
        : enabled && activeDays !== null
          ? `${activeDays} 天`
          : currentValue,
      packageHint: hasPrivilege
        ? '目前顯示的是已綁定延長包狀態，可直接續費或查看待生效方案。'
        : '目前未購買任何延長包，可直接從下方方案快速購買。',
      availableOptions,
      activePeriodText,
      pendingText,
      purchaseDateText,
      expireDateText,
      remainingDaysText,
      statusChips
    };
  }

  private resolveRetentionDisplayValue(
    privilegePayload: RetentionPrivilegePayload | null,
    purchasePayload: RetentionPurchasePayload | null,
    fallbackValue: string
  ): string {
    const activeDays = this.toNullableNumber(privilegePayload?.active?.retentionDays);
    if (activeDays !== null) {
      return `${activeDays} 天`;
    }
    return purchasePayload ? this.resolveRetentionCurrentValue(purchasePayload, fallbackValue) : fallbackValue;
  }

  private resolveRetentionDescription(
    privilegePayload: RetentionPrivilegePayload | null,
    purchasePayload: RetentionPurchasePayload | null,
    fallbackCurrentValue: string,
    fallbackDescription: string
  ): string {
    const activeDays = this.toNullableNumber(privilegePayload?.active?.retentionDays);
    if (activeDays !== null) {
      return `目前已延長至 ${activeDays} 天，可續費延長包或查看到期資訊`;
    }

    if (privilegePayload?.unpaidOrder && !this.isRetentionUnpaidOrderClosed(privilegePayload.unpaidOrder)) {
      const unpaidDays = this.toNullableNumber(privilegePayload.unpaidOrder.retentionDays);
      return unpaidDays !== null
        ? `目前有 ${unpaidDays} 天延長包待支付，可先完成付款後再查看其他方案`
        : '目前有延長包訂單待支付，可先完成付款後再查看其他方案';
    }

    const pendingDays = this.toNullableNumber((privilegePayload?.queuedAddon ?? privilegePayload?.pending)?.retentionDays);
    if (pendingDays !== null) {
      return `已有 ${pendingDays} 天延長包待生效，期間仍可查看其他延長方案`;
    }

    const optionLabels = (Array.isArray(purchasePayload?.options) ? purchasePayload?.options : [])
      .map(option => this.toRetentionOptionLabel(option))
      .filter((label, index, labels) => !!label && labels.indexOf(label) === index);
    if (optionLabels.length) {
      return `預設保存 ${fallbackCurrentValue}，可購買 ${optionLabels.join(' / ')}`;
    }

    return fallbackDescription;
  }

  private extractRetentionPrivilegePayload(response: unknown): RetentionPrivilegePayload | null {
    const result = (response ?? {}) as Record<string, unknown>;
    const isSuccess = !!result['success'] || result['status'] === 0 || result['status'] === 200;
    if (!isSuccess) {
      return null;
    }
    return ((result['data'] ?? response) as RetentionPrivilegePayload) || null;
  }

  private extractRetentionPurchasePayload(response: unknown): RetentionPurchasePayload | null {
    const result = (response ?? {}) as Record<string, unknown>;
    const isSuccess = !!result['success'] || result['status'] === 0 || result['status'] === 200;
    if (!isSuccess) {
      return null;
    }
    return ((result['data'] ?? response) as RetentionPurchasePayload) || null;
  }

  private buildRetentionPeriodText(snapshot: RetentionPrivilegeSnapshot | null | undefined): string | null {
    if (!snapshot) {
      return null;
    }
    const startTime = this.toNullableNumber(snapshot.activateTime ?? snapshot.startTime ?? snapshot.paidTime);
    const expireTime = this.toNullableNumber(snapshot.expireTime);
    if (!startTime || !expireTime) {
      return null;
    }
    return `${this.formatDateOnly(startTime)} - ${this.formatDateOnly(expireTime)}`;
  }

  private resolveRetentionSnapshotDate(value: unknown): string | null {
    const time = this.toNullableNumber(value);
    return time ? this.formatDateOnly(time) : null;
  }

  private resolveRetentionRemainingDays(value: unknown): string | null {
    const expireTime = this.toNullableNumber(value);
    if (!expireTime) {
      return null;
    }
    const remainMs = expireTime - Date.now();
    const remainDays = Math.ceil(remainMs / (24 * 60 * 60 * 1000));
    return remainDays >= 0 ? `${remainDays} 天` : '已到期';
  }

  private toRetentionOptionLabel(option: RetentionPurchaseOption | null | undefined): string {
    if (!option) {
      return '';
    }

    const directName = this.toNullableString(option.name);
    if (directName) {
      return directName;
    }

    const retentionDays = this.toNullableNumber(option.retentionDays);
    if (retentionDays !== null) {
      return `${retentionDays} 天`;
    }

    const effectValue = this.toNullableString(option.effectValue);
    if (!effectValue) {
      return '';
    }

    const normalizedUnit = String(option.effectUnit ?? '')
      .trim()
      .toUpperCase();
    const unit = normalizedUnit === 'DAY' ? '天' : normalizedUnit || '';
    return `${effectValue}${unit ? ` ${unit}` : ''}`.trim();
  }

  private resolveRetentionActionUrl(detail: Record<string, unknown>, item: QuotaUsageItem): string | null {
    const itemRecord = (item ?? {}) as Record<string, unknown>;
    const directItemUrl = this.pickFirstString([
      itemRecord['retentionPurchaseUrl'],
      itemRecord['purchaseUrl'],
      itemRecord['actionUrl'],
      itemRecord['paymentUrl'],
      itemRecord['payUrl'],
      itemRecord['extensionPurchaseUrl']
    ]);
    if (directItemUrl) {
      return directItemUrl;
    }

    const purchaseUrls = detail['retentionPurchaseUrls'];
    if (purchaseUrls && typeof purchaseUrls === 'object' && !Array.isArray(purchaseUrls)) {
      const normalizedQuotaCode = String(item.quotaCode ?? '')
        .trim()
        .toUpperCase();
      const entries = Object.entries(purchaseUrls as Record<string, unknown>);

      const exactMatch = entries.find(([key]) => String(key).trim().toUpperCase() === normalizedQuotaCode)?.[1];
      const exactUrl = this.toNullableString(exactMatch);
      if (exactUrl) {
        return exactUrl;
      }

      const prefixMatch = entries.find(([key]) => normalizedQuotaCode.includes(String(key).trim().toUpperCase()))?.[1];
      const prefixUrl = this.toNullableString(prefixMatch);
      if (prefixUrl) {
        return prefixUrl;
      }
    }

    return this.pickFirstString([
      detail['retentionPurchaseUrl'],
      detail['purchaseUrl'],
      detail['actionUrl'],
      detail['paymentUrl'],
      detail['payUrl']
    ]);
  }

  openRetentionStatusSummary(card: RetentionCard, chip: RetentionStatusChip): void {
    this.openRetentionPurchase(card, chip.key);
  }

  private isRetentionUnpaidOrderClosed(order: RetentionUnpaidOrderSnapshot | null | undefined): boolean {
    if (!order) {
      return true;
    }
    const statusValues = [order.status, order.latestOrderStatus, order.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    return statusValues.some(status =>
      ['paid', 'success', 'succeeded', 'completed', 'complete', 'done', 'failed', 'canceled', 'cancelled', 'closed', 'replaced'].includes(
        status
      )
    );
  }

  private toRetentionPurchaseCode(value: string | null | undefined): string {
    const normalized = String(value ?? '')
      .trim()
      .toUpperCase();
    if (normalized.includes('MESSAGE')) {
      return 'MESSAGE_RETENTION';
    }
    if (normalized.includes('ATTACHMENT')) {
      return 'ATTACHMENT_RETENTION';
    }
    return '';
  }

  private resolveRetentionCurrentValue(payload: RetentionPurchasePayload, fallbackValue: string): string {
    const currentValue = String(payload?.currentValue ?? '').trim();
    if (currentValue) {
      return currentValue;
    }
    const currentDays = this.toNullableNumber(payload?.currentDays);
    if (currentDays !== null) {
      return `${currentDays} 天`;
    }
    return fallbackValue;
  }

  private getPointDeductHint(item: QuotaUsageItem): string {
    if (!this.isPointDeductPolicy(item)) {
      return '';
    }

    const price = this.toNullableString(item.pointDeductUnitPrice);
    if (!price) {
      return '';
    }

    const quotaCode = String(item.quotaCode ?? '')
      .trim()
      .toUpperCase();
    if (quotaCode === QuotaViewComponent.BROADCAST_LIMIT_CODE) {
      return this.t('quota.view.point-deduct-hint.broadcast', { price });
    }

    const unit = String(item.unit ?? '')
      .trim()
      .toUpperCase();
    if (unit === 'GB') {
      return this.t('quota.view.point-deduct-hint.storage', { price });
    }
    if (unit === '次') {
      return this.t('quota.view.point-deduct-hint.usage', { price });
    }

    const displayUnit = String(item.unit ?? '').trim();
    if (!displayUnit) {
      return '';
    }

    return this.t('quota.view.point-deduct-hint.default', {
      unit: displayUnit,
      price
    });
  }

  private buildQuotaGroups(items: QuotaUsageItem[]): QuotaGroup[] {
    const groups = new Map<string, QuotaGroup>();

    items
      .filter(
        item =>
          !QuotaViewComponent.RETENTION_CODES.some(code =>
            String(item.quotaCode ?? '')
              .toUpperCase()
              .includes(code)
          )
      )
      .forEach(item => {
        const code = String(item.categoryCode ?? '').trim();
        const name = String(item.categoryName ?? '').trim();
        const key = code || name || 'default';
        const title = name || this.resolveCategoryTitle(code, item);
        const description = this.resolveCategoryDescription(code, item);
        if (!groups.has(key)) {
          groups.set(key, { key, title, description, items: [] });
        }
        groups.get(key)?.items.push(item);
      });

    return Array.from(groups.values());
  }

  private extractRecentRecordsPage(response: unknown): RecentRecordPage {
    const result = (response ?? {}) as Record<string, unknown>;
    const isSuccess = !!result['success'] || result['status'] === 0 || result['status'] === 200;
    if (!isSuccess) {
      return {
        items: [],
        total: 0,
        pageIndex: Math.max(this.recentRecordsPageIndex - 1, 0),
        pageSize: this.recentRecordsPageSize,
        hasNextPage: false
      };
    }

    const data = this.pickFirstObject([result['data'], response]);
    const rows = this.pickFirstArray([data?.['items'], data?.['records'], data?.['list']]);

    const items = rows.map(item => {
      const current = (item ?? {}) as Record<string, unknown>;
      const typeKey =
        this.toNullableString(current['auditScene'] ?? current['type'] ?? current['category'] ?? current['sourceType']) || '--';
      const itemKey =
        this.toNullableString(
          current['quotaCode'] ?? current['itemCode'] ?? current['resourceCode'] ?? current['sourceBizId'] ?? current['orderId']
        ) || '';
      const pointsAmount = this.resolveRecentRecordPointsAmount(current);
      const snapshot = this.parseSnapshotObject(current['snapshotJson']);

      return {
        time: this.toNullableNumber(current['occurredAt'] ?? current['time'] ?? current['createTime'] ?? current['occurTime']),
        typeKey,
        type: this.resolveRecentRecordTypeLabel(typeKey),
        itemKey,
        item: this.resolveRecentRecordItemLabel(typeKey, current),
        description: this.toNullableString(current['remark'] ?? current['description'] ?? current['note'] ?? current['failReason']) || '--',
        status: this.resolveRecentRecordStatusLabel(current),
        statusTone: this.resolveRecentRecordStatusTone(current),
        amount: pointsAmount,
        ailePointsAmount: this.resolveRecentRecordAilePointsAmount(current, pointsAmount),
        uuponPointsAmount: this.resolveRecentRecordUuponPointsAmount(current),
        cashAmount: this.resolveRecentRecordCashAmount(current, snapshot)
      };
    });

    const total = this.toNullableNumber(data?.['totalCount'] ?? data?.['total'] ?? data?.['count']) ?? items.length;
    const pageIndex = this.toNullableNumber(data?.['pageIndex']) ?? Math.max(this.recentRecordsPageIndex - 1, 0);
    const pageSize = this.toNullableNumber(data?.['pageSize']) ?? this.recentRecordsPageSize;
    const hasNextPage = this.toBoolean(data?.['hasNextPage']) || (Number.isFinite(total) ? (pageIndex + 1) * pageSize < total : false);

    return {
      items,
      total,
      pageIndex,
      pageSize,
      hasNextPage
    };
  }

  private buildRecentRecordsPayload(): Record<string, unknown> {
    return {
      tenantId: this.tenantId,
      pageIndex: Math.max(this.recentRecordsPageIndex - 1, 0),
      pageSize: this.recentRecordsPageSize,
      direction: 'desc',
      orderBy: 'createTime'
    };
  }

  private applyRecentRecordsPage(page: RecentRecordPage): void {
    this.recentRecords = page.items.map(item => this.decorateRecentRecord(item));
    this.recentRecordsTotal = Math.max(page.total, page.items.length);
    this.recentRecordsPageIndex = Math.max((page.pageIndex ?? 0) + 1, 1);
    this.recentRecordsPageSize = Math.max(page.pageSize ?? this.recentRecordsPageSize, 1);
    this.recentRecordsHasNextPage = page.hasNextPage;
  }

  private resolveRecentRecordTypeLabel(type: string): string {
    const normalized = String(type ?? '')
      .trim()
      .toLowerCase();
    if (normalized === 'admingrant' || normalized.includes('grant')) {
      return this.t('quota.view.records.type.admin-grant');
    }
    if (normalized === 'quotadeduct') {
      return this.t('quota.view.records.type.over-limit-deduct');
    }
    if (normalized === 'ordercompensation' || normalized === 'orderrefund') {
      return this.t('quota.view.records.type.order-compensation');
    }
    if (normalized === 'orderpaid') {
      return this.t('quota.view.records.type.feature-consumption');
    }
    return type || '--';
  }

  private resolveRecentRecordStatusLabel(current: Record<string, unknown>): string {
    const isAdminGrant = this.isAdminGrantRecentRecord(current);
    if (this.isRefundedRecentRecord(current)) {
      return this.t('quota.view.records.status.refunded');
    }
    const normalized = this.normalizeRecentRecordStatus(current);
    if (isAdminGrant) {
      if (normalized === 'success') {
        return this.t('quota.view.records.status.granted');
      }
      if (normalized === 'pending' || normalized === 'processing') {
        return this.t('quota.view.records.status.grant-pending');
      }
      if (normalized === 'failed') {
        return this.t('quota.view.records.status.grant-failed');
      }
    }
    if (normalized === 'success') {
      return this.t('quota.view.records.status.success');
    }
    if (normalized === 'pending') {
      return this.t('quota.view.records.status.pending');
    }
    if (normalized === 'failed') {
      return this.t('quota.view.records.status.failed');
    }
    if (normalized === 'skipped') {
      return this.t('quota.view.records.status.skipped');
    }
    if (normalized === 'booked') {
      return this.t('quota.view.records.status.booked');
    }
    if (normalized === 'processing') {
      return this.t('quota.view.records.status.processing');
    }
    return this.t('quota.view.records.status.unknown');
  }

  private resolveRecentRecordStatusTone(current: Record<string, unknown>): 'green' | 'orange' | 'red' | 'gray' {
    const normalized = this.normalizeRecentRecordStatus(current);
    if (normalized === 'success') {
      return 'green';
    }
    if (normalized === 'pending' || normalized === 'processing') {
      return 'orange';
    }
    if (normalized === 'failed') {
      return 'red';
    }
    if (normalized === 'skipped' || normalized === 'booked') {
      return 'gray';
    }
    return 'gray';
  }

  private normalizeRecentRecordStatus(current: Record<string, unknown>): RecentRecordStatus {
    const rawStatus = this.toNullableString(
      current['status'] ??
        current['statusLabel'] ??
        current['deductStatus'] ??
        current['tradeStatus'] ??
        current['aipoolResponseCode'] ??
        current['aipoolResponseMessage']
    );
    const normalized = String(rawStatus ?? '')
      .trim()
      .toLowerCase();

    switch (normalized) {
      case 'success':
        return 'success';
      case 'pending':
        return 'pending';
      case 'failed':
        return 'failed';
      case 'skipped':
        return 'skipped';
      case 'booked':
        return 'booked';
      case 'processing':
        return 'processing';
      default:
        return 'unknown';
    }
  }

  private isRefundedRecentRecord(current: Record<string, unknown>): boolean {
    const type = String(current['auditScene'] ?? current['type'] ?? current['scene'] ?? '')
      .trim()
      .toLowerCase();
    if (!['orderrefund', 'ordercompensation'].includes(type)) {
      return false;
    }
    if (this.normalizeRecentRecordStatus(current) !== 'success') {
      return false;
    }
    const pointsAmount = this.resolveRecentRecordPointsAmount(current);
    return (pointsAmount ?? 0) > 0;
  }

  private isAdminGrantRecentRecord(current: Record<string, unknown>): boolean {
    const type = String(current['auditScene'] ?? current['type'] ?? current['scene'] ?? '')
      .trim()
      .toLowerCase();
    return type === 'admingrant' || type.includes('grant');
  }

  private resolveRecentRecordPointsAmount(current: Record<string, unknown>): number | null {
    const pointChangeAmount = this.toNullableNumber(current['pointChangeAmount']);
    if (pointChangeAmount !== null) {
      return pointChangeAmount;
    }

    const ailePointsChange = this.toNullableNumber(current['ailePointsChange']) ?? 0;
    const uuponPointsChange = this.toNullableNumber(current['uuponPointsChange']) ?? 0;
    if (ailePointsChange || uuponPointsChange) {
      return ailePointsChange + uuponPointsChange;
    }

    const deductAmount = this.toNullableNumber(current['deductAmount']);
    if (deductAmount !== null) {
      return -Math.abs(deductAmount);
    }

    const grantAmount = this.toNullableNumber(current['grantAmount'] ?? current['amount']);
    if (grantAmount !== null && this.isAdminGrantRecentRecord(current)) {
      return Math.abs(grantAmount);
    }

    const paidAilePoints = this.toNullableNumber(current['paidAilePoints']) ?? 0;
    const paidUuponPoints = this.toNullableNumber(current['paidUuponPoints']) ?? 0;
    if (paidAilePoints || paidUuponPoints) {
      return -Math.abs(paidAilePoints + paidUuponPoints);
    }

    return null;
  }

  private resolveRecentRecordAilePointsAmount(current: Record<string, unknown>, totalPointsAmount: number | null): number | null {
    const ailePointsChange = this.toNullableNumber(current['ailePointsChange']);
    if (ailePointsChange !== null) {
      return ailePointsChange;
    }
    const uuponPointsChange = this.toNullableNumber(current['uuponPointsChange']);
    if (uuponPointsChange === null && totalPointsAmount !== null) {
      return totalPointsAmount;
    }
    return null;
  }

  private resolveRecentRecordUuponPointsAmount(current: Record<string, unknown>): number | null {
    return this.toNullableNumber(current['uuponPointsChange']);
  }

  private resolveRecentRecordCashAmount(current: Record<string, unknown>, snapshot: Record<string, unknown> | null): number | null {
    const directAmount = this.pickFirstNumber([
      current['cashAmount'],
      current['cashAmountChange'],
      current['cashChangeAmount'],
      current['paidCashAmount'],
      current['actualCashAmount'],
      current['actualPaidCashAmount'],
      current['paymentAmount'],
      current['paidAmount'],
      current['payAmount'],
      current['orderAmount'],
      current['actualAmount'],
      current['amountCny'],
      current['priceCny'],
      current['cashPrice'],
      current['cashFee'],
      current['payableCash'],
      current['estimatedPayableCash'],
      snapshot?.['cashAmount'],
      snapshot?.['cashAmountChange'],
      snapshot?.['cashChangeAmount'],
      snapshot?.['paidCashAmount'],
      snapshot?.['actualCashAmount'],
      snapshot?.['actualPaidCashAmount'],
      snapshot?.['paymentAmount'],
      snapshot?.['paidAmount'],
      snapshot?.['payAmount'],
      snapshot?.['orderAmount'],
      snapshot?.['amountCny'],
      snapshot?.['priceCny'],
      snapshot?.['cashPrice'],
      snapshot?.['cashFee'],
      snapshot?.['payableCash'],
      snapshot?.['estimatedPayableCash']
    ]);
    if (directAmount !== null) {
      return directAmount;
    }

    const text = this.toNullableString(current['remark'] ?? current['description'] ?? current['note'] ?? current['failReason']) || '';
    const match = text.match(
      /(?:实付|實付|支付金额|支付金額|现金支付|現金支付|现金补足|現金補足|应付现金|應付現金|现金|現金)\s*[:：]?\s*(\d+(?:\.\d+)?)/
    );
    return match ? this.toNullableNumber(match[1]) : null;
  }

  private resolveRecentRecordItemLabel(type: string, current: Record<string, unknown>): string {
    const normalizedType = String(type ?? '')
      .trim()
      .toLowerCase();
    const quotaName = this.toNullableString(current['quotaName']);
    const itemName = this.toNullableString(current['item'] ?? current['title'] ?? current['resourceName']);
    const quotaCode = this.toNullableString(current['quotaCode'] ?? current['itemCode'] ?? current['resourceCode']);
    const description = this.toNullableString(current['remark'] ?? current['description'] ?? current['note'] ?? current['failReason']);
    const orderId = this.toNullableString(current['orderId']);
    const sourceBizId = this.toNullableString(current['sourceBizId']);

    const quotaDisplayName = this.resolveQuotaCodeLabel(quotaCode);
    const retentionPurchaseCode = this.toRetentionPurchaseCode(quotaCode || sourceBizId || orderId || description);

    if (normalizedType === 'admingrant' || normalizedType.includes('grant')) {
      return this.resolveAdminGrantItemLabel(current) || quotaName || itemName || quotaCode || orderId || '--';
    }

    if (normalizedType === 'orderpaid') {
      if (description?.includes('保存期限延長包') || description?.includes('保存期限延长包')) {
        if (retentionPurchaseCode === 'MESSAGE_RETENTION') {
          return this.t('quota.view.records.item.message-retention-package');
        }
        if (retentionPurchaseCode === 'ATTACHMENT_RETENTION') {
          return this.t('quota.view.records.item.attachment-retention-package');
        }
        return this.t('quota.view.records.item.retention-package');
      }
      if (description?.includes('機器人訂單') || description?.includes('机器人订单')) {
        return this.t('quota.view.records.item.feature-order');
      }
      return quotaDisplayName || itemName || quotaName || orderId || '--';
    }

    return quotaName || quotaDisplayName || itemName || quotaCode || orderId || '--';
  }

  private resolveAdminGrantItemLabel(current: Record<string, unknown>): string | null {
    const rawCode = this.toNullableString(
      current['quotaCode'] ??
        current['itemCode'] ??
        current['resourceCode'] ??
        current['reasonCode'] ??
        current['reason'] ??
        current['sourceType']
    );
    const normalized = String(rawCode ?? '')
      .trim()
      .toLowerCase();
    if (!normalized) {
      return null;
    }

    const labelKeyMap: Record<string, string> = {
      customercompensation: 'tenant.point-grant.reason.customer-compensation',
      customercare: 'tenant.point-grant.reason.customer-compensation',
      servicecare: 'tenant.point-grant.reason.service-compensation',
      servicecompensation: 'tenant.point-grant.reason.service-compensation',
      userretention: 'tenant.point-grant.reason.user-care',
      usercare: 'tenant.point-grant.reason.user-care',
      systemoutagecompensation: 'tenant.point-grant.reason.system-compensation',
      systemcompensation: 'tenant.point-grant.reason.system-compensation',
      operationsincentive: 'tenant.point-grant.reason.operation-reward',
      operationreward: 'tenant.point-grant.reason.operation-reward',
      other: 'tenant.point-grant.reason.other'
    };

    const labelKey = labelKeyMap[normalized];
    return labelKey ? this.t(labelKey) : null;
  }

  private decorateRecentRecord(record: RecentRecord): RecentRecord {
    const itemKey = String(record.itemKey ?? '').trim();
    const normalizedType = String(record.typeKey ?? '')
      .trim()
      .toLowerCase();
    if (normalizedType === 'orderpaid' && this.toRetentionPurchaseCode(itemKey)) {
      return {
        ...record,
        type: this.resolveRecentRecordTypeLabel(record.typeKey)
      };
    }

    const matchedQuota = this.detail?.items?.find(item => String(item.quotaCode ?? '').trim() === itemKey);

    return {
      ...record,
      type: this.resolveRecentRecordTypeLabel(record.typeKey),
      item: matchedQuota?.quotaName || this.resolveQuotaCodeLabel(itemKey) || record.item
    };
  }

  private resolveQuotaCodeLabel(quotaCode: string | null | undefined): string | null {
    const normalized = String(quotaCode ?? '')
      .trim()
      .toUpperCase();
    if (!normalized) {
      return null;
    }

    const labelKeyMap: Record<string, string> = {
      SERVICE_ACCOUNT_LIMIT: 'quota.item.service-account-limit.name',
      PRO_SERVICE_ACCOUNT_LIMIT: 'quota.item.service-account-limit.name',
      EMPLOYEE_LIMIT: 'quota.item.employee-limit.name',
      CUSTOMER_LIMIT: 'quota.item.customer-limit.name',
      ASSISTANT_LIMIT: 'quota.item.assistant-limit.name',
      CHANNEL_BINDING_LIMIT: 'quota.item.channel-binding-limit.name',
      TAG_LIMIT: 'quota.item.tag-limit.name',
      ENTRY_REASON_LIMIT: 'quota.item.entry-reason-limit.name',
      BROADCAST_LIMIT: 'quota.item.broadcast-limit.name',
      STORAGE_SPACE: 'quota.item.storage-space.name',
      MESSAGE_RETENTION: 'quota.item.message-retention.name',
      MESSAGE_RETENTION_DAYS: 'quota.item.message-retention.name',
      ATTACHMENT_RETENTION: 'quota.item.attachment-retention.name',
      ATTACHMENT_RETENTION_DAYS: 'quota.item.attachment-retention.name',
      WEBCHAT_ENTRY_LIMIT: 'quota.item.webchat-entry-limit.name',
      BUSINESS_CARD_TEMPLATE_LIMIT: 'quota.item.business-card-template-limit.name'
    };

    const labelKey = labelKeyMap[normalized];
    if (!labelKey) {
      return null;
    }

    return this.t(labelKey);
  }

  private buildSummaryMetrics(detail: Record<string, unknown>, items: QuotaUsageItem[]): SummaryMetric[] {
    const effectiveRates = items
      .filter(item => !this.isPointDeductPolicy(item))
      .map(item => Number(item.usageRate))
      .filter(rate => Number.isFinite(rate));
    const usageRate = this.usageSummary?.overallUsageRate ?? (effectiveRates.length ? Math.max(...effectiveRates) : null);
    const monthDeductPoints =
      this.usageSummary?.monthDeductPoints ??
      Math.abs(
        this.recentRecords
          .filter(record => this.isCurrentMonth(record.time) && Number(record.amount) < 0)
          .reduce((sum, record) => sum + Math.abs(record.amount ?? 0), 0)
      );
    const expireHint =
      this.toNullableString(detail['expiryReminder'] ?? detail['expireHint'] ?? detail['renewalHint']) ||
      (this.retentionCards[0]?.currentValue ? this.retentionCards[0].currentValue : this.t('quota.view.summary.no-expiry'));

    return [
      {
        labelKey: 'quota.view.summary.balance',
        value: this.formatNumber(this.pointBalance.ailePoint),
        hint: this.pointBalance.aipoolTenantId ? `AIPool: ${this.pointBalance.aipoolTenantId}` : this.t('quota.view.summary.balance-hint'),
        tone: 'blue'
      },
      {
        labelKey: 'quota.view.summary.usage-rate',
        value: this.formatUsageRate(usageRate),
        hint: this.t('quota.view.summary.usage-rate-hint', { count: this.riskSummary?.highUsageCount ?? 0 }),
        tone: 'orange'
      },
      {
        labelKey: 'quota.view.summary.month-deduct',
        value: this.formatNumber(monthDeductPoints),
        hint: this.t('quota.view.summary.month-deduct-hint'),
        tone: 'red'
      },
      {
        labelKey: 'quota.view.summary.expiry',
        value: expireHint,
        hint: this.t('quota.view.summary.expiry-hint'),
        tone: 'green'
      }
    ];
  }

  private resolveCategoryTitle(categoryCode: string, item: QuotaUsageItem): string {
    const normalized = categoryCode.toUpperCase();
    if (normalized.includes('RESOURCE')) return this.t('quota.view.category.resource');
    if (normalized.includes('USAGE')) return this.t('quota.view.category.usage');
    if (normalized.includes('RETENTION')) return this.t('quota.view.category.retention');
    if (normalized.includes('PACKAGE')) return this.t('quota.view.category.package');

    const quotaCode = String(item.quotaCode ?? '').toUpperCase();
    if (quotaCode.includes('SPACE') || quotaCode.includes('LIMIT') || quotaCode.includes('COUNT')) {
      return this.t('quota.view.category.resource');
    }
    return this.t('quota.view.category.uncategorized');
  }

  private resolveCategoryDescription(categoryCode: string, item: QuotaUsageItem): string {
    const normalized = categoryCode.toUpperCase();
    if (normalized.includes('RESOURCE')) return this.t('quota.view.category.resource-desc');
    if (normalized.includes('USAGE')) return this.t('quota.view.category.usage-desc');
    if (normalized.includes('RETENTION')) return this.t('quota.view.category.retention-desc');
    if (normalized.includes('PACKAGE')) return this.t('quota.view.category.package-desc');

    if (
      String(item.quotaCode ?? '')
        .toUpperCase()
        .includes('RETENTION')
    ) {
      return this.t('quota.view.category.retention-desc');
    }
    return this.t('quota.view.category.uncategorized-desc');
  }

  private isCurrentMonth(time: number | null | undefined): boolean {
    if (!time) {
      return false;
    }
    const current = new Date();
    const target = new Date(time);
    return current.getFullYear() === target.getFullYear() && current.getMonth() === target.getMonth();
  }

  private toAuthorizationStatus(value: unknown): AuthorizationStatus {
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase();
    if (normalized === 'active') return 'active';
    if (normalized === 'revoked') return 'revoked';
    if (normalized === 'expired') return 'expired';
    if (normalized === 'pending') return 'pending';
    return 'unknown';
  }

  private pickFirstBoolean(values: unknown[]): boolean | null {
    for (const value of values) {
      const parsed = this.toBooleanOrNull(value);
      if (parsed !== null) {
        return parsed;
      }
    }
    return null;
  }

  private toBooleanOrNull(value: unknown): boolean | null {
    if (value === true || value === false) {
      return value;
    }

    const normalized = String(value ?? '')
      .trim()
      .toLowerCase();

    if (!normalized) {
      return null;
    }

    if (['true', '1', 'yes', 'y'].includes(normalized)) {
      return true;
    }

    if (['false', '0', 'no', 'n'].includes(normalized)) {
      return false;
    }

    return null;
  }

  private toNullableString(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    return normalized ? normalized : null;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  }

  private formatSmallGbValue(value: string | number | null | undefined, unit?: string | null): string | null {
    const normalizedUnit = String(unit ?? '')
      .trim()
      .toUpperCase();
    const gbValue = this.toNullableNumber(value);
    if (normalizedUnit !== 'GB' || gbValue === null || gbValue <= 0 || gbValue >= 1) {
      return null;
    }

    const units = ['GB', 'MB', 'KB', 'Bytes'];
    let amount = gbValue;
    let unitIndex = 0;

    while (amount < 1 && unitIndex < units.length - 1) {
      amount *= 1024;
      unitIndex += 1;
    }

    return `${this.formatCompactDecimal(amount)} ${units[unitIndex]}`;
  }

  private formatCompactDecimal(value: number): string {
    return value.toFixed(2).replace(/\.?0+$/, '');
  }

  private toBoolean(value: unknown): boolean {
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private pickFirstObject(values: unknown[]): Record<string, unknown> | null {
    for (const value of values) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
      }
    }
    return null;
  }

  private pickFirstArray(values: unknown[]): unknown[] {
    for (const value of values) {
      if (Array.isArray(value)) {
        return value;
      }
    }
    return [];
  }

  private pickFirstString(values: unknown[]): string | null {
    for (const value of values) {
      const normalized = this.toNullableString(value);
      if (normalized) {
        return normalized;
      }
    }
    return null;
  }

  private pickFirstNumber(values: unknown[]): number | null {
    for (const value of values) {
      const normalized = this.toNullableNumber(value);
      if (normalized !== null) {
        return normalized;
      }
    }
    return null;
  }

  private parseSnapshotObject(value: unknown): Record<string, unknown> | null {
    const directObject = this.pickFirstObject([value]);
    if (directObject) {
      return directObject;
    }

    const normalized = this.toNullableString(value);
    if (!normalized) {
      return null;
    }

    try {
      const parsed = JSON.parse(normalized);
      return this.pickFirstObject([parsed]);
    } catch {
      return null;
    }
  }

  private resetState(): void {
    this.detail = null;
    this.pointBalance = {
      ailePoint: null,
      uuponPoint: null,
      aipoolTenantId: null,
      lastSyncTime: null,
      entryUrl: null
    };
    this.riskSummary = null;
    this.usageSummary = null;
    this.summaryMetrics = [];
    this.authorization = null;
    this.quotaGroups = [];
    this.retentionCards = [];
    this.numericAddonCards = [];
    this.recentRecords = [];
    this.recentRecordsLoading = false;
    this.recentRecordsTotal = 0;
    this.recentRecordsPageIndex = 1;
    this.recentRecordsPageSize = 20;
    this.recentRecordsHasNextPage = false;
  }
}
